// @flow
import React from 'react';
import Dropzone from 'react-dropzone';

import SendButton from './SendButton';
import MentionButton from './MentionButton';
import ContentEditable, { DEFAULT_CONTENT } from './ContentEditable';
import type { Member } from '../../models/group_chats/Member';
import { Mention, AttachmentMention } from './Mention';

type Props = {
  members: Array<Member>,
  onChange?: (e: any) => void,
  onAttachmentChange?: (attachment: any) => void,
  onSubmit: (e: any) => void,
  onKeyUp: (e: KeyboardEvent) => void,
  isMobile: boolean,
  chatBoxHeight: number,
};

type State = {
  content: string,
  caretPosition: number,
  isEmpty: boolean,
  inputFocused: boolean,
  mentionedMembers: Array<Member>,
  isDrag: boolean,
  attachment: any,
};

const ATTACHMENT_HTML_REGEX = /<span(?:".*?"|'.*?'|[^>]*?) +class="attachment"+>(.+?)<\/span>/g;

export default class ChatBoxInput extends React.Component<Props, State> {
  input: any;
  attachmentNode: any;

  constructor(props: Props) {
    super(props);
    this.state = {
      content: DEFAULT_CONTENT,
      caretPosition: 0,
      isEmpty: true,
      inputFocused: false,
      mentionedMembers: [],
      isDrag: false,
      attachment: null,
    };
  }

  reset() {
    this.attachmentNode.value = null;
    this.setState({
      attachment: null,
      content: DEFAULT_CONTENT,
      mentionedMembers: [],
      isEmpty: true,
      isDrag: false,
    });
  }

  getValue() {
    return this.input ? this.input.toRawMessage() : '';
  }

  getAttachment() {
    return (
      this.state.attachment ||
      (this.attachmentNode && this.attachmentNode.files[0])
    );
  }

  handleContentChange(value: string) {
    this.setState({ content: value, isEmpty: this.input.isEmpty() });
  }

  handleInputFocus = () => {
    if (!this.props.isMobile) return;
    const { chatBoxHeight } = this.props;
    this.setState(
      oldState => ({
        ...oldState,
        inputFocused: !oldState.inputFocused,
      }),
      () => {
        const footer = document.querySelector('.mobile-footer');
        const pageTop = document.querySelector('#pageTop');
        const chatBox: ?HTMLElement = document.querySelector('.chat-box');

        const { inputFocused } = this.state;
        if (!inputFocused && chatBox != null) {
          chatBox.style.height = `${chatBoxHeight}px`;
          window.scrollTo(0, 0);
        }

        if (footer) {
          footer.style.display = this.state.inputFocused ? 'none' : 'block';
          if (window.innerWidth < window.innerHeight && chatBox != null) {
            const body: ?HTMLBodyElement = document.body;
            if (footer.style.display === 'none' && body != null) {
              window.scrollTo(0, body.scrollHeight);
              chatBox.style.height = `${body.scrollHeight}px`;
            }
          }
        }
        if (pageTop)
          pageTop.style.visibility = this.state.inputFocused ? 'hidden' : '';
        // Delay scroll to top to make onclick event works
        window.setTimeout(() => {
          if (this.props.isMobile && !this.state.inputFocused) {
            const page = document.querySelector('html');
            if (page != null) page.scrollTop = 0;
          }
        }, 50);
      },
    );
  };

  handleAttachmentChange = () => {
    const attachment = this.attachmentNode.files[0] || this.state.attachment;
    this.setState(
      prevState => {
        return {
          ...prevState,
          content: this.genMentionContent(
            prevState.content.replace(ATTACHMENT_HTML_REGEX, ''),
            AttachmentMention.from(attachment),
          ),
          isEmpty: false,
        };
      },
      () => {
        this.props.onAttachmentChange &&
          this.props.onAttachmentChange(attachment);
      },
    );
  };

  handleMentionClick({ mentionId }: { mentionId: number, type: string }) {
    if (this.state.mentionedMembers.find(member => member.id == mentionId))
      return;
    const member = this.props.members.find(member => member.id == mentionId);
    if (member) this.mentionMember(member);
  }

  mentionMember(member: Member) {
    this.setState(prevState => {
      return {
        ...prevState,
        content: this.genMentionContent(
          prevState.content,
          Mention.from(member),
        ),
        isEmpty: false,
        mentionedMembers: [...prevState.mentionedMembers, member],
      };
    });
  }

  genMentionContent(prevContent: string, mention: Mention): string {
    if (prevContent.length == 0) {
      return `<p>${mention.render()}</p>`;
    } else if (this.input.isContainNoText()) {
      let wrappedMention = `<p>${mention.render()}</p>`;
      return this.removeEmptyPTagIfAny(prevContent) + wrappedMention;
    } else {
      return this.insertAt(
        prevContent,
        this.getMentionPosition(mention),
        mention.render(),
      );
    }
  }

  getMentionPosition(mention: Mention) {
    return mention.type != 'file'
      ? this.input.getActualCaretPos(this.state.caretPosition)
      : 0;
  }

  isContentNormalize(content: string): boolean {
    return !/&nbsp;|\u202F|\u00A0/.test(content);
  }

  insertAt(s: string, i: number, add: string) {
    let matches = s.match(/&nbsp;|\u202F|\u00A0/g);
    matches &&
      matches.forEach(m => {
        i += m.length - 1;
      });
    return this.removeBrTagIfAny(s.substr(0, i)) + add + s.substr(i);
  }

  removeBrTagIfAny(str: string) {
    const pivot = str.length - '<br>'.length;
    return str.substr(pivot, str.length) == '<br>' ? str.substr(0, pivot) : str;
  }

  removeEmptyPTagIfAny(content: string) {
    return content.replace(/<p><br><\/p>/, '');
  }

  handleModalClose() {
    this.input.focus();
  }

  handleSelectedMembersChange = (selectedMembers: Array<Member>) => {
    let caretPosition = this.state.caretPosition;
    let content = this.state.content;
    let mentionedMembers = this.state.mentionedMembers;
    for (let member of selectedMembers) {
      if (!mentionedMembers.includes(member)) {
        let mention = Mention.from(member);
        caretPosition += mention.textContent.length;
        content = this.genMentionContent(content, mention);
        mentionedMembers.push(member);
      }
    }
    for (let member of mentionedMembers) {
      if (!selectedMembers.includes(member)) {
        let name = Mention.from(member).textContent;
        if (
          this.input.textContent().indexOf(name) + name.length <=
          caretPosition
        ) {
          caretPosition -= name.length;
        }
        this.input.removeMentionNodeById(member.id);
        content = this.input.content();
      }
    }
    this.setState({
      mentionedMembers: selectedMembers,
      caretPosition: caretPosition,
      content: content,
      isEmpty: selectedMembers.length === 0 && this.input.isEmpty(),
    });
  };

  handleNodeRemove(node: any) {
    if (node.attributes['data-type'].value == 'file') {
      this.attachmentNode.value = null;
      this.setState({
        attachment: null,
        content: this.input.content(),
      });
    } else {
      this.setState(prevState => ({
        ...prevState,
        mentionedMembers: prevState.mentionedMembers.filter(
          m => m.id != node.id,
        ),
        content: this.input.content(),
      }));
    }
  }

  onBlur() {
    this.setState({
      caretPosition: this.input.getCaretCharacterOffsetWithin(),
    });
  }

  onKeyUp(e: KeyboardEvent) {
    this.onBlur();
    this.props.onKeyUp(e);
  }

  renderPlaceholder(isMobile: boolean) {
    if (isMobile) {
      return (
        <div className="chat-box__placeholder--mobile chat-box__placeholder-container">
          メッセージを入力
        </div>
      );
    } else {
      return (
        <div className="chat-box__placeholder-container">
          <p className="chat-box__placeholder">メッセージを入力</p>
          <p className="chat-box__placeholder">Shift + Enterで送信</p>
        </div>
      );
    }
  }

  onUploadFile(file: any) {
    this.attachmentNode.value = null;
    this.setState(
      {
        attachment: file[0],
        isDrag: false,
      },
      () => {
        this.handleAttachmentChange();
      },
    );
  }

  render() {
    const { members, isMobile, onSubmit } = this.props;
    const { mentionedMembers, isEmpty, content, isDrag } = this.state;
    return (
      <div>
        <div className="chat-box__input-row">
          <div className="left">
            <input
              type="file"
              id="attachment"
              name="attachment"
              onChange={this.handleAttachmentChange}
              ref={e => (this.attachmentNode = e)}
              disabled={this.getAttachment()}
            />
            <label htmlFor="attachment">
              <i className="fas fa-paperclip" />
            </label>
            <MentionButton
              members={members}
              selectedMembers={mentionedMembers}
              onSelectedMembersChange={this.handleSelectedMembersChange}
              onModalClose={() => this.handleModalClose()}
            />
          </div>
          <SendButton
            disabled={!this.getAttachment() && isEmpty}
            mobile={isMobile}
            onClick={onSubmit}
          />
        </div>
        <div className="chat-box__input-container">
          {!isDrag && (
            <ContentEditable
              ref={e => (this.input = e)}
              className={
                'chat-box__input' + (isMobile ? ' chat-box__input--mobile' : '')
              }
              html={content}
              onChange={e => this.handleContentChange(e.target.value)}
              onKeyUp={e => this.onKeyUp(e)}
              onMouseUp={() => this.onBlur()}
              onFocus={this.handleInputFocus}
              onBlur={this.handleInputFocus}
              onNodeRemove={node => this.handleNodeRemove(node)}
              spellCheck="true"
              autoCorrect="off"
              autoComplete="off"
              onDragEnter={() => this.setState({ isDrag: true })}
            />
          )}
          {isEmpty && !isDrag && this.renderPlaceholder(isMobile)}
          {isDrag && (
            <Dropzone
              onDrop={file => this.onUploadFile(file)}
              onDragLeave={() => this.setState({ isDrag: false })}
              className="drop-zone"
            >
              <div className="drop-zone__content">
                <i
                  className="fas fa-cloud-upload-alt fa-3x"
                  aria-hidden="true"
                />
                <p>アップロードしたいファイルをドラッグ&ドロップしてください</p>
              </div>
            </Dropzone>
          )}
        </div>
      </div>
    );
  }
}
